import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { fontMerriweather, theme } from "shared/lib/styles";

export const Container = styled.div`
  padding-block: 64px 79px;
  padding-inline: 0;

  overflow: hidden;
  ${theme.breakpoints.up("tablet")} {
    padding-block: 64px 51px;
    padding-inline: 0;
  }
`;

export const TextWrapper = styled.div`
  margin-block-end: 34px;

  text-align: center;
  ${theme.breakpoints.up("tablet")} {
    margin-block-end: 38px;
  }
`;

export const Title = styled.div`
  margin-block-end: 8px;

  font-weight: 900;
  font-size: 20px;
  font-family: ${fontMerriweather};
  line-height: 19px;

  ${theme.breakpoints.up("tablet")} {
    margin-block-end: 20px;

    font-size: 32px;
    line-height: 38.4px;
  }
`;

export const Description = styled.div`
  max-inline-size: 295px;
  margin-block: 0;
  margin-inline: auto;

  color: #001246;
  font-weight: 300;
  font-size: 15px;
  line-height: 18px;
  span {
    font-weight: 700;
  }

  ${theme.breakpoints.up("tablet")} {
    max-inline-size: 521px;
    margin-block: 0 68.35px;
    margin-inline: auto;

    font-size: 20px;
    line-height: 24px;
  }
`;

type ImageContainerProps = {
  $isActive?: boolean;
};

export const ImageContainer = styled.div<ImageContainerProps>`
  position: relative;

  inline-size: 335px;
  margin-block: 0;
  margin-inline: auto;

  opacity: 0;
  ${theme.breakpoints.up("tablet")} {
    inline-size: 629.6px;
  }
  ${theme.breakpoints.up("desktop")} {
    inline-size: 783px;
  }
  ${props =>
    props.$isActive
      ? css`
          opacity: 1;
        `
      : null}
`;

export const Circle = styled.div`
  position: absolute;

  border-radius: 50%;

  background: #ffca05;
`;

type BigCircleProps = {
  $isActive?: boolean;
};

export const BigCircle = styled(Circle)<BigCircleProps>`
  inset-block-start: -22px;
  inset-inline-start: 147px;

  inline-size: 42px;
  block-size: 42px;

  opacity: 0.3;
  transform: scale(1);

  @keyframes flash {
    0% {
      opacity: 0.3;
      transform: scale(1);
    }
    66.6% {
      opacity: 0.3;
      transform: scale(1);
    }
    68.25% {
      opacity: 0;
      transform: scale(1);
    }
    74% {
      opacity: 0;
      transform: scale(0.5);
    }
    81.25% {
      opacity: 0.3;
      transform: scale(1);
    }
    87.5% {
      opacity: 0;
      transform: scale(1);
    }
    92.75% {
      opacity: 0;
      transform: scale(0.5);
    }
    100% {
      opacity: 0.3;
      transform: scale(1);
    }
  }

  @keyframes flashOnce {
    0% {
      opacity: 0;
      transform: scale(1);
    }
    66.6% {
      opacity: 0;
      transform: scale(1);
    }
    74% {
      opacity: 0;
      transform: scale(0.5);
    }
    81.25% {
      opacity: 0.3;
      transform: scale(1);
    }
    87.5% {
      opacity: 0;
      transform: scale(1);
    }
    92.75% {
      opacity: 0;
      transform: scale(0.5);
    }
    100% {
      opacity: 0.3;
      transform: scale(1);
    }
  }

  ${props =>
    props.$isActive
      ? css`
          animation:
            flash 3s linear 3s infinite,
            flashOnce 3s linear 1;
        `
      : null};

  ${theme.breakpoints.up("tablet")} {
    inset-block-start: -23px;
    inset-inline-start: calc(50% - 44.8px / 2 + 4.8px);

    inline-size: 44.8px;
    block-size: 44.8px;
  }
  ${theme.breakpoints.up("desktop")} {
    inset-block-start: -26px;
    inset-inline-start: 366px;

    inline-size: 56px;
    block-size: 56px;
  }
`;

type SmallCircleProps = {
  $isActive?: boolean;
};

export const SmallCircle = styled(Circle)<SmallCircleProps>`
  inset-block-start: -9px;
  inset-inline-start: 160px;
  z-index: 1;

  inline-size: 16px;
  block-size: 16px;

  @keyframes smallCircleFlash {
    0% {
      opacity: 0;
    }
    66.6% {
      opacity: 0;
    }
    75% {
      opacity: 1;
    }
    100% {
      opacity: 1;
    }
  }

  ${props =>
    props.$isActive
      ? css`
          animation: smallCircleFlash 3s linear 1;
        `
      : null};

  ${theme.breakpoints.up("tablet")} {
    inset-inline-start: calc(50% - 17.23px / 2 + 5.02px);

    inline-size: 17.23px;
    block-size: 17.23px;
  }
  ${theme.breakpoints.up("desktop")} {
    inset-inline-start: 383px;

    inline-size: 21.54px;
    block-size: 21.54px;
  }
`;

type ImageComputerProps = {
  $isActive?: boolean;
};

export const ImageComputer = styled.img<ImageComputerProps>`
  position: relative;

  inline-size: 100%;
  max-inline-size: 275px;

  @keyframes computerFadeIn {
    0% {
      opacity: 0;
      transform: translateY(30px);
    }
    33.3% {
      opacity: 1;
      transform: translateY(0);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${props =>
    props.$isActive
      ? css`
          animation: computerFadeIn 3s linear 1;
        `
      : null};

  ${theme.breakpoints.up("tablet")} {
    max-inline-size: 523.2px;
  }
  ${theme.breakpoints.up("desktop")} {
    max-inline-size: 654px;
  }
`;

type ImagePhonesProps = {
  $isActive?: boolean;
};

export const ImagePhones = styled.img<ImagePhonesProps>`
  position: absolute;
  inset-block-start: -43.5px;
  inset-inline-end: -68px;

  inline-size: 100%;
  max-inline-size: 257px;

  opacity: 1;

  @keyframes phonesFadeIn {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    30% {
      opacity: 0;
    }
    66.6% {
      opacity: 1;
      transform: translateY(0);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${props =>
    props.$isActive
      ? css`
          animation: phonesFadeIn 3s linear 1;
        `
      : null};

  ${theme.breakpoints.up("tablet")} {
    inset-block-start: -62.5px;
    inset-inline-end: -112px;

    max-inline-size: 447px;
  }
  ${theme.breakpoints.up("desktop")} {
    inset-block-start: -90.5px;
    inset-inline-end: -154px;

    max-inline-size: 589px;
  }
`;
